import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { Users, CreditCard, Clock, ArrowLeft, Info } from "lucide-react";
import { getPaymentMethods, requestTourPayment } from "../actions/paymentAction";
import { getBookingDetail } from "../actions/tour/tourAction";
import { Paths } from "../constants/Paths";
import Loading from "../common/Loading";
import { FiInbox } from "react-icons/fi";
import FonepayPayment from "../components/FonepayDynamicQRPayment";
import { format } from "date-fns";
import useSEO from "../hooks/useSEO";

export default function TourPayment() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSEO } = useSEO();

  useEffect(() => {
    setSEO(`Payment | Yatra Tickets`);
  }, [setSEO]);

  const initialData = location.state || {};
  const [tourBookId, setTourBookId] = useState(initialData.tourBookId);
  const [bookUID, setBookUID] = useState(initialData.bookUID);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);

  const {
    // Use the state bookUID
    packageID,
    tourPricingID,
    packageTitle,
    totalFare,
    travelerCount = 1,
    pricePerPerson,
    image,
    startplace,
    endplace,
    departureDate,
    returnDate,
    tourType,
    currency = "NPR",
  } = bookingDetails || initialData;

  const [paymentOptions, setPaymentOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [showDynamicQR, setShowDynamicQR] = useState(null);

  useEffect(() => {
    if (!bookUID && !tourBookId) {
      navigate(Paths.MY_BOOKINGS, { replace: true });
      toast({
        variant: "destructive",
        title: "No Booking Found",
        description: "Please complete a booking before accessing payment.",
      });
      return;
    }

    // Fetch booking details
    setIsLoadingDetails(true);
    dispatch(getBookingDetail(bookUID || tourBookId, (res) => {
      if (res) {
        setBookUID(res.BookUID);
        setTourBookId(res.TourBookID);
        const pkgFiles = res.tourPackFiles || [];
        const tourImage = pkgFiles.length > 0 ? pkgFiles[0].FileURL : null;

        setBookingDetails({
          tourBookId: res.TourBookID,
          bookUID: res.BookUID,
          packageID: res.PackageID,
          tourPricingID: res.TourPricingID,
          packageTitle: res.PackageTitle,
          totalFare: res.TotalFare,
          travelerCount: res.TotalBookedSeat || 1,
          pricePerPerson: res.TotalFare,
          image: tourImage,
          startplace: res.DestinationName,
          departureDate: res.DepartureDate ? format(new Date(res.DepartureDate), "dd MMM yyyy") : "",
          tourType: res.IsGroup ? "Group Tour" : "Fixed Departure",
        });
      }
      setIsLoadingDetails(false);
    }));

    dispatch(
      getPaymentMethods(false, (methods) => {
        setPaymentOptions(methods || []);
        setIsLoading(false);
      })
    );
  }, [tourBookId, dispatch, navigate]);

  // DOM form helpers
  const createDom = (type, attr = {}) => {
    const el = document.createElement(type);
    Object.keys(attr).forEach((key) => el.setAttribute(key, attr[key]));
    return el;
  };
  const addFormInput = (inputs = [], form) => {
    inputs.forEach(({ key, val }) => {
      form.appendChild(createDom("input", { type: "hidden", name: key, id: key, value: val }));
    });
  };

  const goToBookingDetail = () =>
    navigate(Paths.TOUR_BOOKING_DETAIL.replace(":bookUID", bookUID));

  const handlePayNow = () => {
    if (!selectedMethod) {
      toast({
        variant: "destructive",
        title: "No Payment Method Selected",
        description: "Please select a payment method before continuing.",
      });
      return;
    }
    const payload = {
      bookingRef: String(bookUID),
      gatewayID: selectedMethod,
      successFullURL:
        window.location.origin +
        Paths.TOUR_BOOKING_DETAIL.replace(":bookUID", bookUID),
      tranID: localStorage.transId ?? "",
      orderEntity: 3,
    };
    dispatch(
      requestTourPayment(payload, (res) => {
        if (res?.IsSuccess) {
          if (res.ActionName === "dynamicQr") {
            setShowDynamicQR(res);
          } else if (res.ActionName === "renderElement") {
            const fm = createDom("form", {
              method: "POST",
              action: res.Result.requestURL + (res.Result.query ?? ""),
            });
            if (res.Result.inputs) addFormInput(res.Result.inputs, fm);
            document.body.appendChild(fm);
            fm.submit();
          }
        } else {
          toast({
            variant: "destructive",
            title: "Payment Failed",
            description: res?.Message || "Could not initiate payment. Please try again.",
          });
        }
      })
    );
  };

  // Use pricePerPerson if available, else back-calculate from totalFare
  const perPerson = pricePerPerson || (totalFare && travelerCount ? totalFare / travelerCount : 0);
  const grandTotal = perPerson * travelerCount;

  if (isLoadingDetails) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loading label="Fetching your booking details..." />
      </div>
    );
  }

  if (showDynamicQR) {
    return (
      <FonepayPayment
        rs={showDynamicQR}
        pricing={{
          Currency: currency,
          GTotalFare: grandTotal,
          TourBookID: tourBookId,
          BookUID: bookUID,
          PackageID: packageID,
          TourPricingID: tourPricingID
        }}
      />
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-background min-h-[80vh]">
      <div className="container mx-auto p-4 lg:p-8 max-w-7xl">

        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>

        {/* Step indicator */}
        <div className="flex items-center gap-3 mb-6">
          <Badge variant="outline" className="gap-1 text-xs">
            <span className="h-4 w-4 rounded-full bg-muted text-muted-foreground text-[10px] flex items-center justify-center font-bold">1</span>
            Traveler Details
          </Badge>
          <div className="h-px flex-1 bg-border" />
          <Badge className="gap-1 text-xs bg-primary">
            <span className="h-4 w-4 rounded-full bg-white text-primary text-[10px] flex items-center justify-center font-bold">2</span>
            Payment
          </Badge>
          <div className="h-px flex-1 bg-border" />
          <Badge variant="outline" className="gap-1 text-xs text-muted-foreground">
            <span className="h-4 w-4 rounded-full bg-muted text-muted-foreground text-[10px] flex items-center justify-center font-bold">3</span>
            Confirmation
          </Badge>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">

          {/* ── Left Column: Payment Options ── */}
          <div className="lg:col-span-2 space-y-6">

            {/* Pay Now Card */}
            <Card className="shadow-sm">
              <CardHeader className="pb-4 border-b">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg">Pay Now</CardTitle>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Select a payment method to complete your booking instantly.
                </p>
              </CardHeader>
              <CardContent className="pt-5">
                {isLoading ? (
                  <Loading label="Fetching payment options... Please wait!" />
                ) : paymentOptions.filter((o) => o.CheckoutType !== "PayLater").length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground">
                    <FiInbox className="w-16 h-16 mx-auto mb-3 text-gray-400" />
                    <p className="font-medium">No payment options available</p>
                    <p className="text-sm mt-1">Please contact us for assistance.</p>
                  </div>
                ) : (
                  <>
                    <RadioGroup
                      className="grid grid-cols-2 sm:grid-cols-3 gap-4"
                      onValueChange={setSelectedMethod}
                    >
                      {paymentOptions
                        .filter((o) => o.CheckoutType !== "PayLater")
                        .map((option) => (
                          <div key={option.GatewayID}>
                            <RadioGroupItem
                              value={option.GatewayID}
                              id={`gateway-${option.GatewayID}`}
                              className="peer sr-only"
                            />
                            <Label
                              htmlFor={`gateway-${option.GatewayID}`}
                              className="flex flex-col items-center justify-between rounded-lg border-2 border-muted bg-background p-4 cursor-pointer hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary h-full transition-all"
                            >
                              {option.LogoUrl && (
                                <img
                                  src={option.LogoUrl}
                                  alt={option.GatewayDisplayName}
                                  className="h-10 object-contain mb-2"
                                />
                              )}
                              <span className="text-sm font-medium text-center">
                                {option.GatewayDisplayName}
                              </span>
                              {option.Description?.length > 0 && (
                                <p className="text-xs text-muted-foreground text-center mt-1">
                                  {option.Description}
                                </p>
                              )}
                            </Label>
                          </div>
                        ))}
                    </RadioGroup>
                    <Button className="w-full mt-6 h-12 text-base" onClick={handlePayNow}>
                      Continue to Pay
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Pay Later Card */}
            <Card className="shadow-sm border-dashed border-amber-300 dark:border-amber-700">
              <CardHeader className="pb-4 border-b">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-500" />
                  <CardTitle className="text-lg">Pay Later</CardTitle>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Your booking is confirmed. You can complete the payment at a later time.
                </p>
              </CardHeader>
              <CardContent className="pt-5">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 mb-4">
                  <Clock className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Payment must be completed before your departure date to confirm your seats.
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="w-full h-12 text-base border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400"
                  onClick={goToBookingDetail}
                >
                  Skip Payment — Pay Later
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* ── Right Column: Sidebar ── */}
          <div className="space-y-6">

            {/* 1. Tour Summary Card */}
            <Card className="shadow-sm">
              <CardContent className="p-4">
                <div className="flex gap-4 mb-4">
                  <div className="h-16 w-16 bg-gray-200 rounded-md shrink-0 overflow-hidden">
                    <img
                      src={image || "https://placehold.co/100x100?text=Tour"}
                      alt="Tour"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-bold line-clamp-2 text-sm">
                      {packageTitle || "Tour Package"}
                    </h3>
                    {(bookUID || tourBookId) && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Ref: <span className="font-mono font-medium">{bookUID || tourBookId}</span>
                      </p>
                    )}
                  </div>
                </div>

                <Separator className="my-3" />

                <div className="space-y-3 text-sm">
                  {startplace && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-muted-foreground font-medium">Starts in</span>
                      <div className="col-span-2 text-right">
                        <div className="font-semibold">{startplace}</div>
                        {departureDate && (
                          <div className="text-xs text-muted-foreground">{departureDate}</div>
                        )}
                      </div>
                    </div>
                  )}
                  {endplace && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-muted-foreground font-medium">Ends</span>
                      <div className="col-span-2 text-right">
                        <div className="font-semibold">{endplace}</div>
                        {returnDate && (
                          <div className="text-xs text-muted-foreground">{returnDate}</div>
                        )}
                      </div>
                    </div>
                  )}
                  {tourType && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-muted-foreground font-medium">Tour Type</span>
                      <div className="col-span-2 text-right font-semibold">
                        {String(tourType) === "Group Tour" ? "Group Tour" : "Fixed Departure"}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-muted-foreground font-medium">Travelers</span>
                    <div className="col-span-2 text-right font-semibold flex items-center justify-end gap-1">
                      <Users className="h-3 w-3" /> {travelerCount}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 2. Price Breakdown Card */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3 border-b">
                <CardTitle className="text-base">Price Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <div>
                    <div className="font-medium">Brochure Price</div>
                    <div className="text-xs text-muted-foreground">
                      NPR {perPerson.toLocaleString()} × {travelerCount} traveler
                    </div>
                  </div>
                  <div className="font-medium">
                    NPR {Math.round(grandTotal).toLocaleString()}
                  </div>
                </div>

                <Separator />

                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-bold text-lg block">Total Price</span>
                    <span className="text-[10px] text-muted-foreground block">(Airfare Included)</span>
                  </div>
                  <span className="font-bold text-xl text-primary">
                    NPR {Math.round(grandTotal).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="font-medium">Deposit Payable Now</span>
                  <span className="font-bold">
                    NPR {Math.round(grandTotal).toLocaleString()}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* 3. Need Help Card */}
            <Card className="shadow-sm">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 font-bold text-sm">
                  <span className="bg-green-100 text-green-700 p-1 rounded-full text-xs">?</span>
                  Need Help?
                </div>
                <p className="text-xs text-muted-foreground">
                  Questions about your booking? We're here to help.
                </p>
                <Button
                  variant="secondary"
                  className="w-full text-sm bg-primary text-white hover:bg-primary/80 h-10"
                  onClick={() => (window.location.href = Paths.CONTACT_US)}
                >
                  Contact Support
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
